import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { getFreeTrialStatus } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { FREE_TRIAL_2026, fetchFreeTrialLength } from './helpers'
import { TrialStatus } from '@/reducers/auth/userReducer'

/**
 * The trial length the kill switches actually honor, or `undefined` when the code is
 * unrecognized or its switch is off. Says nothing about whether *this* user may still
 * redeem one — post-payment copy has a trial to name even though the membership has
 * left `unredeemed`.
 */
export const useHonoredFreeTrialLength = (
  trialCode: string | null
): number | undefined => {
  // Not `useLoadedFlagValue`: it nulls without a targeting key, and the first signup
  // steps run before an account exists — the switch would be stuck open on exactly the
  // pages that advertise the trial. Default is open so an outage cannot kill the funnel.
  const freeTrial2026Enabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableFreeTrial2026,
    true
  )

  if (trialCode === FREE_TRIAL_2026 && !freeTrial2026Enabled) {
    return undefined
  }

  return fetchFreeTrialLength(trialCode)
}

/**
 * Every signup step reads trial eligibility from here so the advertised trial
 * cannot disagree with what checkout charges — the API applies the same kill
 * switch when it builds the Stripe session.
 */
export const useFreeTrialEnabled = (trialCode: string | null): boolean => {
  const userFreeTrialStatus = useReselector(getFreeTrialStatus)
  const honoredLength = useHonoredFreeTrialLength(trialCode)

  return (
    honoredLength !== undefined &&
    userFreeTrialStatus === TrialStatus.unredeemed
  )
}
